import { getAddArtworkDocumentUrl, getAddArtworkImageUrl, getArtworkUrl, getArtworkVisibilityUrl, getArtworksUrl, getCreateArtworkUrl, getDeleteArtworkUrl, getPublicArtworksUrl, getTokenizeArtworkUrl } from "../endpoints";
import { useQuery, useMutation, useQueryClient } from "react-query";
import useAxios from "../../hooks/useAxios";


export const useGetArtwork = (code) => {
    const client = useAxios();
    return useQuery(['getArtwork', code], () => {
        return client.get(getArtworkUrl(code));
    });
}

export const useGetArtworks = () => {
    const client = useAxios();
    return useQuery('getArtworks', () => {
        return client.get(getArtworksUrl());
    });
};

export const useGetPublicArtworks = () => {
    const client = useAxios();
    return useQuery('getPublicArtworks', () => {
        return client.get(getPublicArtworksUrl());
    });
};

export const useCreateArtwork = () => {
    const queryClient = useQueryClient();
    const client = useAxios();
    return useMutation((artwork) => {
        return client.post(getCreateArtworkUrl(), artwork);
    }, {
        onMutate: async(artwork) => {
            await queryClient.cancelQueries('getArtworks');
            const previousArtworks = queryClient.getQueryData('getArtworks');
            queryClient.setQueryData('getArtworks', (prev) => {
                return {
                    ...prev,
                    data: [...prev.data, {
                        title: artwork.get("title"),
                        images: []
                    }]
                };
            });
            return {previousArtworks}; // to revert in case of error
        },
        onError: (_error, _artwork, context) => {
            queryClient.setQueryData('getArtworks', context.previousArtworks);
        },
        onSettled: () => {
            queryClient.invalidateQueries('getArtworks');
        }
    });
};

export const useSetArtworkVisibility = () => {
    const queryClient = useQueryClient();
    const client = useAxios();
    return useMutation((data) => {
        return client.patch(getArtworkVisibilityUrl(data.code), {isPublic: data.isPublic});
    }, {
        onMutate: async (data) => {
            await queryClient.cancelQueries('getPublicArtworks');
            await queryClient.cancelQueries(['getArtwork', data.code]);

            const previousArtworks = queryClient.getQueryData('getPublicArtworks');
            const previousArtwork = queryClient.getQueryData(['getArtwork', data.code]);

            if(!!previousArtworks) queryClient.setQueryData('getPublicArtworks', (prev) => {
                return {
                    ...prev,
                    data: [...prev.data].map((artwork) => {
                        if(artwork.code === data.code) {
                            return {...artwork, public: data.isPublic};
                        }
                        return artwork;
                    })
                };
            });

            queryClient.setQueryData(['getArtwork', data.code], (prev) => {
                return {
                    ...prev,
                    data: {
                        ...prev.data,
                        public: data.isPublic
                    }
                };
            });

            return {previousArtworks, previousArtwork}; // to revert in case of error
        },
        onError: (_error, _artwork, context) => {
            queryClient.setQueryData('getPublicArtworks', context.previousArtworks);
            queryClient.setQueryData(['getArtwork', context.previousArtwork?.code], context.previousArtwork);
        },
        onSettled: (_data, _error, _vars, context) => {
            queryClient.invalidateQueries('getPublicArtworks');
            queryClient.invalidateQueries(['getArtwork', context.previousArtwork?.code]);
        }
    });
};

export const useTokenizeArtwork = () => {
    const client = useAxios();
    return useMutation((code) => {
        return client.post(getTokenizeArtworkUrl(code));
    });
};

export const useDeleteArtwork = () => {
    const queryClient = useQueryClient();
    const client = useAxios();
    return useMutation((code) => {
        return client.delete(getDeleteArtworkUrl(code));
    }, {
        onMutate: async (code) => {
            await queryClient.cancelQueries('getArtworks');
            const previousArtworks = queryClient.getQueryData('getArtworks');
            queryClient.setQueryData('getArtworks', (prev) => {
                return {
                    ...prev,
                    data: [...prev.data].filter((artwork) => artwork.code !== code)
                };
            });
            return {previousArtworks}; // to revert in case of error
        },
        onError: (_error, _artwork, context) => {
            queryClient.setQueryData('getArtworks', context.previousArtworks);
        },
        onSettled: () => {
            queryClient.invalidateQueries('getArtworks');
        }
    });
};

export const useAddArtworkImage = () => {
    const queryClient = useQueryClient();
    const client = useAxios();
    return useMutation((data) => {
        console.log(data.fd);
        return client.post(getAddArtworkImageUrl(data.code), data.fd);
    }, {
        onMutate: async (data) => {
            await queryClient.cancelQueries(['getArtwork', data.code]);
            const previousArtwork = queryClient.getQueryData(['getArtwork', data.code]);
            queryClient.setQueryData(['getArtwork', data.code], (prev) => {
                return {
                    ...prev,
                    data: {
                        ...prev.data,
                        images: [...prev.data.images, data.fd.get('images')]
                    }
                };
            });
            return {previousArtwork}; // to revert in case of error
        },
        onError: (_error, _artwork, context) => {
            queryClient.setQueryData(['getArtwork', context.previousArtwork?.code], context.previousArtwork);
        },
        onSettled: (_data, _error, _vars, context) => {
            queryClient.invalidateQueries(['getArtwork', context.previousArtwork?.code]);
        }
    });
}

export const useAddArtworkDocument = () => {
    const queryClient = useQueryClient();
    const client = useAxios();
    return useMutation((data) => {
        return client.post(getAddArtworkDocumentUrl(data.code), data.fd);
    }, {
        onMutate: async (data) => {
            await queryClient.cancelQueries(['getArtwork', data.code]);
            const previousArtwork = queryClient.getQueryData(['getArtwork', data.code]);
            queryClient.setQueryData(['getArtwork', data.code], (prev) => {
                return {
                    ...prev,
                    data: {
                        ...prev.data,
                        documents: [...prev.data.documents, data.fd.get('documents')]
                    }
                };
            });
            return {previousArtwork}; // to revert in case of error
        },
        onError: (_error, _artwork, context) => {
            queryClient.setQueryData(['getArtwork', context.previousArtwork?.code], context.previousArtwork);
        },
        onSettled: (_data, _error, _vars, context) => {
            queryClient.invalidateQueries(['getArtwork', context.previousArtwork?.code]);
        }
    });
}